import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import type { PageHeaderCrumb } from "@outpave/ds";
import { useSubNav } from "context/SubNavContext";

/**
 * Breadcrumbs for a page nested under a nav parent: `Parent > pageLabel`.
 *
 * The parent step opens that parent's sub-nav rather than linking, because a
 * grouping segment (`/admin/settings/`) has no route of its own. It is derived
 * from the route and the shell's nav config, so a page names only itself and a
 * nav change cannot leave a stale parent behind.
 *
 * Returns just the page step when the route has no nav parent.
 */
export function useNavBreadcrumbs(
  pageLabel: PageHeaderCrumb["label"],
): PageHeaderCrumb[] {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { items, subItems, showSubNav } = useSubNav();

  const [, , parentUrl, subUrl] = pathname.split("/");
  const parent = items.find(
    (item) => (item.urlToMatch ?? item.url) === parentUrl,
  );
  const pageCrumb: PageHeaderCrumb = { label: pageLabel };
  if (!parent || !subUrl) return [pageCrumb];

  const children = subItems.filter(
    (sub) => sub.parentUrl === parent.url && sub.isVisible !== false,
  );
  // Same rule the rail applies: an item that collapses to its only child has
  // no sub-nav to open, so the step stays inert rather than doing nothing.
  const opensSubNav =
    children.length > (parent.collapseWhenSingleSubItem ? 1 : 0);

  return [
    {
      label: t(parent.name),
      onClick: opensSubNav ? () => showSubNav(parent.url) : undefined,
    },
    pageCrumb,
  ];
}

export default useNavBreadcrumbs;
